/**
 * @file graph-search-logic.ts
 * @description Fonctions pures extraites de GraphManager.searchInternetMessageIdsFromViewFilter.
 *
 * Ces fonctions ne font aucun appel réseau et ne dépendent d'aucun état externe.
 * Elles sont exportées séparément pour pouvoir être testées unitairement.
 *
 * GraphManager.ts continue à implémenter la logique d'orchestration (appels API, batching,
 * pagination) et appellera ces fonctions pour les parties calculatoires.
 */

import type { ViewFilter } from "../../customView";

// ─────────────────────────────────────────────────────────────────────────────
// Types internes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Représente un item brut retourné par Graph API (avant mapping vers DataSearchResult).
 * Seuls les champs utilisés par la logique de consolidation sont typés.
 */
export type RawGraphItem = {
    internetMessageId: string;
    sentDateTime: string;
    [key: string]: any;
};

/**
 * Flags de contrôle dérivés du ViewFilter et de l'email de l'utilisateur courant.
 * Pilotent toutes les branches de l'algorithme de recherche.
 */
export type SearchFlags = {
    /** Expéditeur inclut l'utilisateur courant (→ recherche dans sentItems) */
    hasSelfSender: boolean;
    /** Expéditeur inclut des personnes autres que l'utilisateur courant (→ recherche dans inbox) */
    hasOthersSenders: boolean;
    /** Requête à exécuter en batch inbox + sentItems */
    sendersBatchNeeded: boolean;
    /** Pas d'expéditeur spécifié + destinataires présents → chercher dans les 2 dossiers */
    searchBothFoldersForRecipients: boolean;
    /** Pas d'expéditeur ni destinataire, mais d'autres filtres → chercher dans les 2 dossiers */
    searchBothFoldersNoParticipants: boolean;
    /** Fragment $filter pour l'expéditeur = utilisateur courant */
    selfSenderFilterString: string;
    /** Fragment $filter pour les expéditeurs ≠ utilisateur courant */
    othersSenderFilterString: string;
    /** Au moins un filtre non-date est actif (readStatus, hasAttachment, senders) */
    hasAnyFilterExceptDate: boolean;
};

/**
 * Résultat de la consolidation finale.
 */
export type ConsolidationInput = {
    mainResults: RawGraphItem[];
    searchResults: RawGraphItem[];
    mainWasExecuted: boolean;
    noSearchResults: boolean;
    inboxRecipientsSkipped: boolean;
    keywordsNeeded: boolean;
};

// ─────────────────────────────────────────────────────────────────────────────
// 0. escapeODataStringValue
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Échappe une valeur destinée à un littéral string OData (ex: `$filter=x eq 'valeur'`).
 *
 * Règle OData : à l'intérieur des guillemets simples, on échappe UNIQUEMENT l'apostrophe
 * en la doublant (`'` → `''`). Les autres caractères doivent rester BRUTS.
 *
 * ⚠️ Ne JAMAIS appliquer `encodeURIComponent` sur ces valeurs : l'encodage de transport
 * (espaces, `=`, `+`, `/`…) doit être délégué à `URLSearchParams` au moment de construire
 * l'URL — c'est la seule construction qu'aucune pile (browser / OkHttp / NSURLSession) ne
 * « corrige » au passage. Attention : la forme produite n'est PAS identique partout (web `+`,
 * React Native `%20`) — Graph accepte les deux sur un appel direct.
 * Un `encodeURIComponent` manuel produit des `%3D`/`%2B` qui cassent l'appel sur iOS.
 *
 * Cette fonction ne fait QUE l'échappement sémantique OData ; elle ne fait pas d'encodage URL.
 */
export function escapeODataStringValue(value: string): string {
    return value.replace(/'/g, "''");
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. isRecipientsOnlySearch
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Détermine si la recherche ne porte QUE sur les destinataires (aucun autre filtre actif).
 * Dans ce cas, la mainQuery est skippée entièrement.
 *
 * Catalogue : cas D1, D2, D3
 */
export function isRecipientsOnlySearch(viewFilter: ViewFilter): boolean {
    return (
        !!viewFilter.identifiedRecipients &&
        viewFilter.identifiedRecipients.length > 0 &&
        (!viewFilter.identifiedSenders ||
            viewFilter.identifiedSenders.length === 0) &&
        (!viewFilter.keywords || viewFilter.keywords.length === 0) &&
        !viewFilter.startDate &&
        !viewFilter.endDate &&
        !viewFilter.readStatus &&
        viewFilter.hasAttachment === undefined &&
        (!viewFilter.categories || viewFilter.categories.length === 0) &&
        (!viewFilter.mailStatus || viewFilter.mailStatus.length === 0)
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. resolveSearchFlags
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calcule tous les flags de contrôle à partir du ViewFilter et de l'email courant.
 * Ces flags pilotent : quels dossiers chercher, si un batch est nécessaire, etc.
 *
 * Reproduit fidèlement les blocs de décision de la section #PARTIE $FILTER de GraphManager.
 */
export function resolveSearchFlags(
    viewFilter: ViewFilter,
    currentUserEmail: string,
): SearchFlags {
    let hasSelfSender = false;
    let hasOthersSenders = false;
    let sendersBatchNeeded = false;
    let searchBothFoldersForRecipients = false;
    let searchBothFoldersNoParticipants = false;
    let selfSenderFilterString = "";
    let othersSenderFilterString = "";
    let hasAnyFilterExceptDate = false;

    // readStatus et hasAttachment comptent comme filtres non-date
    if (viewFilter.readStatus) {
        hasAnyFilterExceptDate = true;
    }
    if (viewFilter.hasAttachment !== undefined) {
        hasAnyFilterExceptDate = true;
    }

    if (
        viewFilter.identifiedSenders &&
        viewFilter.identifiedSenders.length > 0
    ) {
        hasSelfSender = viewFilter.identifiedSenders.some(
            (u) => u.toLowerCase() === currentUserEmail.toLowerCase(),
        );
        const sendersOthers = viewFilter.identifiedSenders.filter(
            (u) => u.toLowerCase() !== currentUserEmail.toLowerCase(),
        );
        hasOthersSenders = sendersOthers.length > 0;

        if (hasSelfSender) {
            const escapedSelf = currentUserEmail.replace(/'/g, "''");
            selfSenderFilterString = `contains(from/emailAddress/address,'${escapedSelf}')`;
        }

        if (hasOthersSenders) {
            const conditions = sendersOthers.map((u) => {
                const escaped = u.replace(/'/g, "''");
                return `contains(from/emailAddress/address,'${escaped}')`;
            });
            othersSenderFilterString =
                conditions.length === 1
                    ? conditions[0]
                    : `(${conditions.join(" or ")})`;
        }

        sendersBatchNeeded = true;
        hasAnyFilterExceptDate = true;
    } else if (
        (!viewFilter.identifiedSenders ||
            viewFilter.identifiedSenders.length === 0) &&
        viewFilter.identifiedRecipients &&
        viewFilter.identifiedRecipients.length > 0
    ) {
        // Pas d'expéditeurs mais des destinataires → chercher inbox ET sentItems
        searchBothFoldersForRecipients = true;
        hasSelfSender = true;
        hasOthersSenders = true;
        sendersBatchNeeded = true;
    } else if (
        (!viewFilter.identifiedSenders ||
            viewFilter.identifiedSenders.length === 0) &&
        (!viewFilter.identifiedRecipients ||
            viewFilter.identifiedRecipients.length === 0) &&
        (hasAnyFilterExceptDate ||
            (viewFilter.keywords && viewFilter.keywords.length > 0))
    ) {
        // Ni expéditeurs ni destinataires mais d'autres filtres → chercher les deux dossiers
        searchBothFoldersNoParticipants = true;
        sendersBatchNeeded = true;
    }

    return {
        hasSelfSender,
        hasOthersSenders,
        sendersBatchNeeded,
        searchBothFoldersForRecipients,
        searchBothFoldersNoParticipants,
        selfSenderFilterString,
        othersSenderFilterString,
        hasAnyFilterExceptDate,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. buildFilterParts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Construit le tableau de fragments pour le paramètre $filter.
 * Les fragments sont joints par ' and ' dans la requête finale.
 *
 * NOTE : un fallback de date (1900-01-01) est toujours ajouté si aucune date n'est fournie,
 * pour garantir la compatibilité avec $orderby.
 *
 * NOTE : les filtres expéditeurs (contains from) NE sont PAS inclus ici car ils sont
 * gérés séparément par les variantes inbox/sentItems du batch.
 */
export function buildFilterParts(viewFilter: ViewFilter): string[] {
    const parts: string[] = [];

    if (viewFilter.startDate) {
        const d = new Date(viewFilter.startDate);
        if (!isNaN(d.getTime())) {
            parts.push(`sentDateTime ge ${d.toISOString()}`);
        }
    }

    if (viewFilter.endDate) {
        const d = new Date(viewFilter.endDate);
        if (!isNaN(d.getTime())) {
            parts.push(`sentDateTime le ${d.toISOString()}`);
        }
    }

    if (!viewFilter.startDate && !viewFilter.endDate) {
        parts.push(`sentDateTime ge 1900-01-01T00:00:00Z`);
    }

    if (viewFilter.readStatus === "read") parts.push(`isRead eq true`);
    if (viewFilter.readStatus === "unread") parts.push(`isRead eq false`);

    if (viewFilter.hasAttachment !== undefined) {
        parts.push(`hasAttachments eq ${viewFilter.hasAttachment}`);
    }

    return parts;
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. encodeKqlSearchValue
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Encodage minimal pour la valeur du paramètre $search KQL dans les requêtes batch Graph API.
 * Graph API requiert les caractères Unicode (é, è, ü…) en clair dans l'URL du batch JSON.
 * Seul " doit être encodé car invalide brut dans une query string.
 *
 * Comportement validé : (%22cybersécurité%22) ✅  vs  (%22cybersecurit%C3%A9%22) ❌
 */
export function encodeKqlSearchValue(kql: string): string {
    return kql.replace(/"/g, "%22");
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. buildKeywordsSearchParts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transforme la liste de keywords en fragments KQL pour $search.
 * Chaque keyword produit une requête $search séparée (elles sont exécutées individuellement
 * puis leurs résultats sont réunis par UNION avant intersection avec la mainQuery).
 *
 * Format KQL obligatoire : ("keyword") — les guillemets activent le mode avancé
 * quand dupliqués : ("kw") OR ("kw") ou ("kw") AND ("kw")
 */
export function buildKeywordsSearchParts(keywords: string[]): string[] {
    return keywords.map((keyword) => {
        const clean = keyword.replace(/"/g, '\\"');
        return `("${clean}")`;
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. buildRecipientsSearchParts
// ─────────────────────────────────────────────────────────────────────────────

export type ResolvedRecipientsSearch = {
    parts: string[];
    needed: boolean;
};

/**
 * Construit les fragments KQL pour $search destinataires.
 * Reproduit la logique de la section #8 de GraphManager.
 *
 * Règle : si le seul destinataire est l'utilisateur courant ET qu'on cherche dans inbox,
 * les emails reçus par lui sont déjà dans la mainQuery inbox → pas besoin de $search inbox.
 * Seul le sentItems search est utile (emails que l'utilisateur s'est envoyés à lui-même).
 * SAUF si searchBothFoldersForRecipients est actif.
 */
export function buildRecipientsSearchParts(
    recipients: string[],
    currentUserEmail: string,
    flags: Pick<
        SearchFlags,
        "hasSelfSender" | "hasOthersSenders" | "searchBothFoldersForRecipients"
    >,
): ResolvedRecipientsSearch {
    if (!recipients || recipients.length === 0) {
        return { parts: [], needed: false };
    }

    const currentUserLower = currentUserEmail.toLowerCase();
    const currentUserInRecipients = recipients.some(
        (u) => u.toLowerCase() === currentUserLower,
    );

    const willSearchInbox =
        flags.hasOthersSenders ||
        flags.searchBothFoldersForRecipients ||
        !flags.hasSelfSender; // fallback : si pas de senders du tout

    if (
        currentUserInRecipients &&
        willSearchInbox &&
        !flags.searchBothFoldersForRecipients
    ) {
        // Utilisateur est destinataire ET on cherche dans inbox, mais pas en mode bothFolders
        if (flags.hasSelfSender) {
            // hasSelfSender → on cherche aussi dans sentItems → recipients search utile
            const parts = recipients.map(
                (u) => `"recipients:${u.replace(/"/g, '\\"')}"`,
            );
            return { parts, needed: true };
        } else {
            // Inbox suffit, pas besoin de $search recipients
            return { parts: [], needed: false };
        }
    } else {
        // Logique normale ou searchBothFolders
        const parts = recipients.map(
            (u) => `"recipients:${u.replace(/"/g, '\\"')}"`,
        );
        return { parts, needed: true };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. shouldSkipInboxRecipientsSearch
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Détermine si la recherche $search recipients dans inbox doit être skippée.
 *
 * Cas : tous les destinataires sont l'utilisateur courant ET on est en mode expéditeurs explicites
 * → inbox mainQuery les capture déjà. Seul sentItems search reste utile.
 *
 * Quand ce flag est true → la consolidation finale utilise UNION au lieu d'INTERSECTION.
 *
 * IMPORTANT : retourne toujours false si searchBothFoldersForRecipients=true.
 * Dans ce mode (recipients + autres filtres, sans senders), la consolidation doit être
 * INTERSECTION — les deux dossiers sont cherchés explicitement dans le batch recipients.
 */
export function shouldSkipInboxRecipientsSearch(
    recipientsSearchParts: string[],
    currentUserEmail: string,
    flags: Pick<
        SearchFlags,
        | "hasOthersSenders"
        | "searchBothFoldersNoParticipants"
        | "searchBothFoldersForRecipients"
    >,
): boolean {
    if (flags.searchBothFoldersForRecipients) return false;

    const recipientsForInbox = recipientsSearchParts.filter((recipient) => {
        // Extrait l'email depuis la syntaxe "recipients:email@domain.com"
        const email = recipient.replace(/^"recipients:|"$/g, "");
        return email.toLowerCase() !== currentUserEmail.toLowerCase();
    });

    return (
        (flags.hasOthersSenders || flags.searchBothFoldersNoParticipants) &&
        recipientsForInbox.length === 0
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. applyDatePostFilter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Filtre les résultats $search par plage de dates côté client.
 *
 * RAISON D'ÊTRE : les requêtes $search ignorent $filter, donc les résultats recipients
 * et keywords arrivent sans contrainte temporelle. Ce post-filtre garantit la cohérence.
 *
 * Catalogue : correction du bug cas D4 (dates + recipients).
 * Statut : fallback de sécurité — la correction algorithmique principale est à venir (Phase 4).
 */
export function applyDatePostFilter(
    results: RawGraphItem[],
    startDate: string | null | undefined,
    endDate: string | null | undefined,
): RawGraphItem[] {
    const startMs = startDate ? new Date(startDate).getTime() : null;
    const endMs = endDate ? new Date(endDate).getTime() : null;

    if (startMs === null && endMs === null) return results;

    return results.filter((item) => {
        const itemMs = new Date(item.sentDateTime).getTime();
        if (startMs !== null && itemMs < startMs) return false;
        if (endMs !== null && itemMs > endMs) return false;
        return true;
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. consolidateSearchResults  (join keywords ∩ recipients)
// ─────────────────────────────────────────────────────────────────────────────

export type SearchConsolidationResult = {
    results: RawGraphItem[];
    /** true quand ni keywords ni recipients → pas de résultats search à consolider */
    noResults: boolean;
};

/**
 * Joint les résultats keywords et recipients en un seul tableau.
 *
 * Règle :
 * - keywords seuls    → résultats keywords
 * - recipients seuls  → résultats recipients
 * - les deux         → INTERSECTION (email doit matcher les deux)
 * - aucun            → noResults = true
 */
export function consolidateSearchResults(
    keywordsResults: RawGraphItem[],
    recipientsResults: RawGraphItem[],
    keywordsNeeded: boolean,
    recipientsNeeded: boolean,
): SearchConsolidationResult {
    if (keywordsNeeded && !recipientsNeeded) {
        return { results: keywordsResults, noResults: false };
    }

    if (!keywordsNeeded && recipientsNeeded) {
        return { results: recipientsResults, noResults: false };
    }

    if (keywordsNeeded && recipientsNeeded) {
        const recipientIds = new Set(
            recipientsResults.map((r) => r.internetMessageId),
        );
        const intersected = keywordsResults.filter((r) =>
            recipientIds.has(r.internetMessageId),
        );
        return { results: intersected, noResults: false };
    }

    return { results: [], noResults: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. consolidateFinalResults  (mainQuery ∩/∪ searchResults)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Consolidation finale entre les résultats mainQuery ($filter) et les résultats $search.
 *
 * Trois modes :
 *
 * A) mainQuery non exécutée → résultats $search seuls
 *    (cas isRecipientsOnly : D1/D2/D3)
 *
 * B) mainQuery exécutée, pas de résultats $search → résultats mainQuery seuls
 *    (cas filtres simples : A1→A8, C1→C8)
 *
 * C) inboxRecipientsSkipped && !keywordsNeeded → UNION
 *    Raisonnement : inbox mainQuery contient déjà tous les mails reçus par l'utilisateur.
 *    On ajoute les mails sentItems où l'utilisateur est destinataire.
 *    ⚠️  BUG CONNU (cas U2) : si mainQuery est vide (plage de dates vide) et que
 *    les résultats $search ne sont pas post-filtrés par date, l'UNION retourne
 *    des résultats hors plage. → Appeler applyDatePostFilter AVANT cette fonction.
 *
 * D) Sinon → INTERSECTION
 *    (cas standard avec expéditeurs + destinataires ou keywords)
 */
export function consolidateFinalResults(
    input: ConsolidationInput,
): RawGraphItem[] {
    const {
        mainResults,
        searchResults,
        mainWasExecuted,
        noSearchResults,
        inboxRecipientsSkipped,
        keywordsNeeded,
    } = input;

    if (!mainWasExecuted) {
        // Cas A : search only
        return searchResults;
    }

    if (noSearchResults) {
        // Cas B : main only
        return mainResults;
    }

    if (inboxRecipientsSkipped && !keywordsNeeded) {
        // Cas C : UNION inbox mainQuery + sentItems search
        const all = [...mainResults, ...searchResults];
        const seen = new Set<string>();
        return all.filter((item) => {
            if (seen.has(item.internetMessageId)) return false;
            seen.add(item.internetMessageId);
            return true;
        });
    }

    // Cas D : INTERSECTION
    const mainIds = new Set(mainResults.map((r) => r.internetMessageId));
    return searchResults.filter((r) => mainIds.has(r.internetMessageId));
}
